import PropTypes from 'prop-types';
import { Box, Divider, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router';
import SectionLabel from '../../Common/SectionLabel';
import FieldRow from '../../Common/FieldRow';
import { formatDate, formatDateTime } from 'utils/utils';
import { TENANT_ADMIN_VISIBILITY_OPTIONS } from 'features/IRWorkstation/utils/alertConstants';

const AlertDetailSection = ({
	event_context = {},
	acknowledgedAt,
	acknowledgedByName,
}) => {
	// Keeping this for now for mocking fake input_signals
	const mockedLabelMap = {
		lp_title: 'LP',
		indication: 'Indication',
		conditions: 'Conditions',
		pipeline_state: 'Pipeline state',
		sessions_today: 'Sessions today',
		total_time: 'Total time',
		sections_accessed: 'Sections accessed',
		vs_cohort_average: 'vs. cohort average',
	};

	const labelMap = {
		...mockedLabelMap,
		stage: 'Stage',
		days_since_last_interaction: 'Days since last interaction',
		last_interaction_date: 'Last interaction date',
		confidence: 'Confidence',
		commitment: 'Commitment',
		account_name: 'Account',
		alert_creation: 'Alert Creation Date/Time',
		crm_report_id: 'CRM Report ID',
		internal_coverage: 'Internal Coverage'
	};

	// Main ordering: Account | Stage | Other
	const getOrderedKeys = (eventContext) => {
		const keys = Object.keys(eventContext);
		const internalCoverageKey = keys.includes('internal_coverage') ? ['internal_coverage'] : [];
		const accountKey = keys.includes('account_name') ? ['account_name'] : [];
		const stageKey = keys.includes('stage') ? ['stage'] : [];
		const otherKeys = keys.filter(
			(key) =>
				key !== 'internal_coverage' &&
				key !== 'account_name' &&
				key !== 'stage' &&
				key !== 'crm_report_id' &&
				key !== 'error_type' &&
				key !== 'expected_column' &&
				key !== 'related_record_link' &&
				key !== 'health_score_id'
		);

		return [...internalCoverageKey, ...accountKey, ...stageKey, ...otherKeys];
	};

	const getErrorKeys = (eventContext) => {
		const keys = Object.keys(eventContext);

		return keys.filter(
			(key) =>
				key === 'crm_report_id' ||
				key === 'error_type' ||
				key === 'expected_column'
		);
	};

	const orderedKeys = getOrderedKeys(event_context);
	const errorKeys = getErrorKeys(event_context);

	const formatValue = (key, value) => {
		if (key === 'related_record' && event_context.related_record_link) {
			return (
				<Link
					component={RouterLink}
					to={event_context.related_record_link}
					sx={{
						color: 'primary.main',
						textDecoration: 'none',
						'&:hover': { textDecoration: 'underline' },
					}}
				>
					{value}
				</Link>
			);
		}

		if (key === 'commitment') {
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			}).format(value);
		}

		if (key === 'last_interaction_date' || key === 'enter_date') {
			return formatDate(value, true);
		}

		if (key === 'alert_creation') {
			return formatDateTime(value);
		}

		if (key === 'days_since_last_interaction') {
			return Math.round(value);
		}

		if (key === 'visibility') {
			return (
				TENANT_ADMIN_VISIBILITY_OPTIONS.find(
					(option) => option.value === String(value).toLowerCase()
				)?.label ?? value
			);
		}

		return value;
	};

	const acknowledgementValue =
		acknowledgedAt && acknowledgedByName
			? `${formatDateTime(acknowledgedAt)} - ${acknowledgedByName}`
			: null;

	return (
		<Box>
			<SectionLabel text='Details' />

			<Box
				sx={{
					backgroundColor:
						errorKeys.length > 0 ? 'background.paper' : 'background.subtle',
					borderRadius: '8px',
					padding: '8px 10px',
				}}
			>
				{orderedKeys.map((key) => {
					const value = event_context[key];

					if (value === null || value === undefined || value === '') {
						return null;
					}

					return (
						<FieldRow
							key={key}
							label={
								labelMap[key] ||
								key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
							}
							value={formatValue(key, value)}
						/>
					);
				})}

				{acknowledgementValue && (
					<FieldRow
						label='Acknowledged'
						value={acknowledgementValue}
						valueColor='green'
					/>
				)}
			</Box>

			{errorKeys.length > 0 && (
				<>
					<Divider sx={{ my: 2 }} />

					<Box
						sx={{
							backgroundColor: 'background.subtle',
							borderRadius: '8px',
							padding: '8px 10px',
						}}
					>
						{errorKeys.map((key) => {
							const value = event_context[key];

							if (value === null || value === undefined || value === '') {
								return null;
							}

							return (
								<FieldRow
									key={key}
									label={
										labelMap[key] ||
										key
											.replace(/_/g, ' ')
											.replace(/\b\w/g, (letter) => letter.toUpperCase())
									}
									value={formatValue(key, value)}
									valueColor={key === 'error_type' ? 'red' : 'text.primary'}
								/>
							);
						})}
					</Box>
				</>
			)}
		</Box>
	);
};

AlertDetailSection.propTypes = {
	event_context: PropTypes.object,
	acknowledgedAt: PropTypes.string,
	acknowledgedByName: PropTypes.string,
};

export default AlertDetailSection;
